import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical,
  Eye,
  Users,
  Calendar,
  XCircle,
  Edit2,
  Trash2,
  Lock,
  BarChart3,
} from "lucide-react";

const EventActionsMenu = ({
  onViewDetails,
  onViewAttendees,
  onCancel,
  onReschedule,
  onEdit,
  onDelete,
  onMarkAsClosed,
  onViewAnalytics,
  align = "right",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
    placement: "bottom",
    maxHeight: 280,
  });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const itemCount = [
    onViewDetails,
    onViewAttendees,
    onViewAnalytics,
    onCancel,
    onReschedule,
    onEdit,
    onDelete,
    onMarkAsClosed,
  ].filter((item) => item !== undefined).length;

  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const itemHeight = 40;
        const dividerHeight = 1;
        const dividers = Math.max(0, itemCount - 1);
        const padding = 0;
        const dropdownHeight =
          itemCount * itemHeight + dividers * dividerHeight + padding;

        const dropdownWidth = 192;
        const spacing = 2;

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        const showAbove =
          spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        let top;
        let maxHeight = dropdownHeight;

        if (showAbove) {
          maxHeight = Math.min(dropdownHeight, spaceAbove - spacing);
          maxHeight = Math.max(maxHeight, 200);
          top = rect.top - dropdownHeight - spacing;
          if (top < 0) {
            top = spacing;
            maxHeight = Math.max(200, rect.top - spacing * 2);
          }
        } else {
          top = rect.bottom + spacing;
          const availableSpace = window.innerHeight - rect.bottom - spacing;
          maxHeight = Math.min(dropdownHeight, availableSpace);
          maxHeight = Math.max(maxHeight, 200);
          if (top + maxHeight > window.innerHeight) {
            maxHeight = Math.max(200, window.innerHeight - top - spacing);
          }
        }

        let left = null;
        let right = null;

        if (align === "right") {
          const buttonRight = rect.right;
          const buttonLeft = rect.left;
          const viewportRight = window.innerWidth;

          const wouldOverflowRight =
            buttonRight + dropdownWidth > viewportRight;
          const rightAlignmentMargin = viewportRight - buttonRight;

          const wouldFitLeft = buttonLeft + dropdownWidth <= viewportRight;
          if (
            wouldOverflowRight ||
            rightAlignmentMargin < 50 ||
            !wouldFitLeft
          ) {
            if (wouldFitLeft) {
              left = buttonLeft;
              right = null;
            } else {
              right = 10;
              left = null;
            }
          } else {
            right = viewportRight - buttonRight + 5;
            left = null;
            if (right < 0) {
              right = 0;
            }
          }
        } else {
          if (rect.left + dropdownWidth > window.innerWidth) {
            right = window.innerWidth - rect.right;
            left = null;
          } else {
            left = rect.left;
            right = null;
          }
        }

        setPosition({
          top,
          left,
          right,
          placement: showAbove ? "top" : "bottom",
          maxHeight: maxHeight,
        });
      }
    };

    if (isOpen) {
      setTimeout(updatePosition, 0);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, align, itemCount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedMenu =
        menuRef.current && menuRef.current.contains(event.target);
      const clickedButton =
        buttonRef.current && buttonRef.current.contains(event.target);

      if (!clickedMenu && !clickedButton) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleAction = (action, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsOpen(false);
    if (action && typeof action === "function") {
      requestAnimationFrame(() => {
        action();
      });
    }
  };

  const menuItems = [
    {
      label: "View details",
      icon: Eye,
      onClick: (e) => handleAction(onViewDetails, e),
      divider: true,
      handler: onViewDetails,
    },
    {
      label: "Attendee",
      icon: Users,
      onClick: (e) => handleAction(onViewAttendees, e),
      divider: true,
      handler: onViewAttendees,
    },
    {
      label: "Analytics",
      icon: BarChart3,
      onClick: (e) => handleAction(onViewAnalytics, e),
      color: "text-indigo-600",
      divider: true,
      handler: onViewAnalytics,
    },
    {
      label: "Cancel",
      icon: XCircle,
      onClick: (e) => handleAction(onCancel, e),
      color: "text-orange-600",
      divider: true,
      handler: onCancel,
    },
    {
      label: "Reschedule",
      icon: Calendar,
      onClick: (e) => handleAction(onReschedule, e),
      color: "text-blue-600",
      divider: true,
      handler: onReschedule,
    },
    {
      label: "Edit",
      icon: Edit2,
      onClick: (e) => handleAction(onEdit, e),
      color: "text-purple-600",
      divider: true,
      handler: onEdit,
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: (e) => handleAction(onDelete, e),
      color: "text-red-600",
      divider: true,
      handler: onDelete,
    },
    {
      label: "Mark as Closed",
      icon: Lock,
      onClick: (e) => handleAction(onMarkAsClosed, e),
      color: "text-gray-600",
      divider: false,
      handler: onMarkAsClosed,
    },
  ].filter((item) => item.handler !== undefined);

  const dropdownContent = isOpen && (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-48 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden"
      style={{
        top: `${position.top}px`,
        ...(position.right !== null && position.right !== undefined
          ? { right: `${position.right}px`, left: "auto" }
          : { left: `${position.left}px`, right: "auto" }),
        maxHeight: `${position.maxHeight}px`,
      }}
    >
      {menuItems.map((item, index) => {
        const isLastItem = index === menuItems.length - 1;
        return (
          <React.Fragment key={index}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (item.onClick) {
                  item.onClick(e);
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              className={`flex items-center w-full px-4 py-2.5 text-sm text-left transition-colors hover:bg-gray-50 ${
                item.color || "text-gray-900"
              }`}
              type="button"
            >
              <item.icon className="mr-3 w-4 h-4 flex-shrink-0" />
              {item.label}
            </button>
            {item.divider && !isLastItem && (
              <div className="h-px bg-gray-200" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="relative inline-block">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 text-gray-600 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
          aria-label="Event actions"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <MoreVertical className="w-5 h-5" />
        </button>
      </div>
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </>
  );
};

export default EventActionsMenu;
